<template>
  <v-row>
    <v-col cols="12">
      <base-card>
        <v-card-title class="pb-0">
          New Job
          <v-spacer />
          <v-spacer />
          <v-spacer />
          <v-spacer />

          <v-btn
            color="success"
            :loading="jobLoading"
            :disabled="!isFormValid || jobLoading"
            @click="startJob"
          >
            <v-icon class="pr-1">
              mdi-content-save
            </v-icon>
            Start Job
          </v-btn>
        </v-card-title>
        <v-row>
          <v-col
            cols="12"
            :md="isSuper ? 6 : 10"
            class="pt-0"
          >
            <v-card-text>
              <v-checkbox
                v-model="productionOnly"
                label="Production templates only"
                dense
                hide-details
                class="mt-0 mb-3"
                @change="productionOnlyChanged"
              />

              <v-select
                v-model="selectedTemplate"
                label="Select Template"
                :items="filteredTemplates"
                item-text="name"
                return-object
                dense
                outlined
                @change="templateChanged"
              >
                <template v-slot:item="{ item }">
                  <div class="template-option">
                    <span class="template-option-name">
                      {{ item.name }}
                    </span>

                    <v-chip
                      v-if="getTemplateProdType(item)"
                      x-small
                      color="primary lighten-5"
                      text-color="primary"
                      class="ml-2"
                    >
                      {{ getTemplateProdType(item) }}
                    </v-chip>
                  </div>
                </template>

                <template v-slot:selection="{ item }">
                  <div class="template-option">
                    <span class="template-option-name">
                      {{ item.name }}
                    </span>

                    <v-chip
                      v-if="getTemplateProdType(item)"
                      x-small
                      color="primary lighten-5"
                      text-color="primary"
                      class="ml-2"
                    >
                      {{ getTemplateProdType(item) }}
                    </v-chip>
                  </div>
                </template>
              </v-select>
              <v-text-field
                v-model.trim="filename"
                label="Output Filename"
                dense
                outlined
                @change="filenameChanged"
              />

              <div
                v-if="selectedTemplate.files && selectedTemplate.files.aep"
                class="pb-2"
              >
                <a
                  href="#"
                  @click.prevent="setScript()"
                >Set script data</a>
                <br>
                {{ selectedTemplate.files.dir }}
                {{ selectedTemplate.files.dir + "/" + selectedTemplate.files.aep }}
              </div>

              <div v-if="selectedTemplate.props && selectedTemplate.props.length">
                <a
                  href="#"
                  @click.prevent="randomData()"
                >Set Random data</a>

                <div
                  v-for="(prop, i) in selectedTemplate.props"
                  :key="prop.layer + i"
                  class="nexrender-prop-row p-b-10"
                >
                  <v-select
                    v-model="prop.type"
                    :items="['Text', 'Image', 'Audio', 'Video']"
                    dense
                    outlined
                    background-color="#eee"
                    hide-details
                    :disabled="isFontProp(prop) || isColorProp(prop)"
                    class="nexrender-prop-type"
                    @change="propChanged(prop)"
                  />

                  <v-autocomplete
                    v-if="isFontProp(prop)"
                    v-model="prop.value"
                    :items="fontFaceOptions"
                    :label="prop.displayName"
                    :loading="fontsLoading"
                    dense
                    outlined
                    clearable
                    hide-details
                    class="nexrender-prop-value"
                    no-data-text="No fonts"
                    @change="propChanged(prop)"
                  />

                  <v-menu
                    v-else-if="isColorProp(prop)"
                    offset-y
                    :close-on-content-click="false"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="getPropColorHex(prop)"
                        :label="prop.displayName"
                        dense
                        outlined
                        readonly
                        hide-details
                        class="nexrender-prop-value"
                        append-icon="mdi-palette"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <template v-slot:prepend-inner>
                          <span
                            class="nexrender-color-swatch"
                            :style="{ backgroundColor: getPropColorHex(prop) }"
                          />
                        </template>
                      </v-text-field>
                    </template>

                    <v-color-picker
                      :value="getPropColorHex(prop)"
                      mode="hexa"
                      hide-mode-switch
                      @input="colorChanged(prop, $event)"
                    />
                  </v-menu>

                  <v-text-field
                    v-else
                    v-model.trim="prop.value"
                    :label="prop.displayName"
                    dense
                    outlined
                    :hint="prop.comp + ' (' + prop.compW + 'x' + prop.compH + '), ' + prop.compDur.toFixed(2) + ' sec'"
                    clearable
                    :append-icon="prop.type !== 'Text' ? 'mdi-format-list-bulleted' : ''"
                    hide-details
                    :loading="prop.fileLoading"
                    class="nexrender-prop-value"
                    @change="propChanged(prop)"
                    @click:append="selectValue(prop, prop.type)"
                  />

                  <div
                    v-if="isUploadProp(prop)"
                    class="nexrender-prop-upload"
                  >
                    <v-file-input
                      v-model="prop.file"
                      hide-input
                      hide-details
                      prepend-icon="mdi-upload"
                      class="p-l-10 p-t-0"
                      accept="image/*,video/*,audio/*"
                      :loading="prop.fileLoading"
                      @change="fileUpload(prop, i)"
                    />
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-col>
          <v-col
            v-if="isSuper"
            cols="12"
            md="6"
            class="pt-0"
          >
            <v-select
              v-model="selectedJobTemplateId"
              :items="jobTemplates"
              item-text="name"
              item-value="id"
              label="Job Template"
              dense
              outlined
              hide-details
              :loading="jobTemplatesLoading"
              :disabled="jobTemplatesLoading || !jobTemplates.length"
              class="mb-3"
              @change="jobTemplateChanged"
            >
              <template v-slot:item="{ item }">
                <div class="job-template-option">
                  <span>{{ item.id }} - {{ item.name }}</span>
                  <v-chip
                    v-if="item.is_default"
                    x-small
                    color="primary"
                    text-color="white"
                    class="ml-2"
                  >
                    Default
                  </v-chip>
                </div>
              </template>

              <template v-slot:selection="{ item }">
                <div class="job-template-option">
                  <span>{{ item.id }} - {{ item.name }}</span>
                  <v-chip
                    v-if="item.is_default"
                    x-small
                    color="primary"
                    text-color="white"
                    class="ml-2"
                  >
                    Default
                  </v-chip>
                </div>
              </template>
            </v-select>

            <base-card>
              <div class="mt-4">
                <div ref="editor" />
              </div>
            </base-card>
          </v-col>
        </v-row>
      </base-card>
    </v-col>

    <v-dialog
      v-model="propSelectionDialog"
      max-width="900"
    >
      <v-card>
        <v-card-title class="text-h5">
          Options
        </v-card-title>

        <v-card-text style="max-height: 65vh; overflow-y: auto;">
          <a
            v-for="(asset, index) in currentAssets"
            :key="index"
            href="#"
            class="hovered-link d-block text-truncate"
            @click.prevent="setProp(asset, propSelectionType)"
          >
            {{ asset }}
          </a>

          <div
            v-if="currentAssets.length === 0"
            class="text--secondary py-4"
          >
            No data
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            depressed
            text
            @click="propSelectionDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
  import { basicSetup, EditorView } from 'codemirror'
  import { json, jsonParseLinter } from '@codemirror/lang-json'
  import { Compartment } from '@codemirror/state'
  import { linter } from '@codemirror/lint'
  import { processAEPProject } from '@/store/converters'
  import moment from 'moment-timezone'

  const languageConf = new Compartment()
  const linterExtension = linter(jsonParseLinter())

  const videoAssets = [
    'file:///N:/samples/video_samples/vid01.mp4',
    'file:///N:/samples/video_samples/vid02.mp4',
    'file:///N:/samples/video_samples/vid03.mp4',
    'file:///N:/samples/video_samples/vid10.mp4',
    'file:///N:/samples/video_samples/vid11.mp4',
    'file:///N:/samples/video_samples/vid12.mp4',
    'file:///N:/samples/video_samples/vid13.mp4',
    'file:///N:/samples/video_samples/vid14.mp4',
    'file:///N:/samples/video_samples/vid15.mp4',
    'file:///N:/samples/video_samples/vid16.mp4',
    'file:///N:/samples/video_samples/vid17.mp4',
    'file:///N:/samples/video_samples/vid18.mp4',
    'file:///N:/samples/video_samples/vid19.mp4',
    'file:///N:/samples/video_samples/vid20.mp4',
    'https://storage007.noisypeak.com/mastervod/nexrender/ads_placeholder_bg_video.mp4',
  ]

  const audioAssets = [
    'https://storage007.noisypeak.com/mastervod/nexrender/music_by_lakey_inspired.mp3',
  ]

  const imageAssets = [
    'file:///N:/samples/img_samples/img1.jpg',
    'file:///N:/samples/img_samples/img2.jpg',
    'file:///N:/samples/img_samples/img3.jpg',
    'file:///N:/samples/img_samples/img4.jpg',
    'file:///N:/samples/img_samples/img5.jpg',
    'file:///N:/samples/img_samples/img6.jpg',
    'file:///N:/samples/img_samples/img7.jpg',
    'file:///N:/samples/img_samples/img8.jpg',
    'file:///N:/samples/img_samples/img9.jpg',
    'file:///N:/samples/img_samples/img10.jpg',
    'file:///N:/samples/img_samples/img11.jpg',
    'file:///N:/samples/img_samples/img12.jpg',
    'file:///N:/samples/img_samples/img13.jpg',
    'file:///N:/samples/img_samples/img14.jpg',
    'file:///N:/samples/img_samples/img15.jpg',

    'file:///N:/samples/logo_samples/horizontal_timber.eps',
    'file:///N:/samples/logo_samples/horizontal_timber.png',
    'file:///N:/samples/logo_samples/round_bmw.eps',
    'file:///N:/samples/logo_samples/round_bmw.png',
    'file:///N:/samples/logo_samples/square_lv.eps',
    'file:///N:/samples/logo_samples/square_lv.png',
    'file:///N:/samples/logo_samples/vertical_levis.eps',
    'file:///N:/samples/logo_samples/vertical_levis.png',
    'file:///N:/samples/logo_samples/vertical_polo.eps',
    'file:///N:/samples/logo_samples/vertical_polo.png',
    'https://storage007.noisypeak.com/mastervod/nexrender/Noisypeak_Playout_Logo.png',
  ]

  function randomInteger (min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min
  }

  export default {
    name: 'Nexrender',
    metaInfo: {
      title: 'Nexrender',
    },
    data () {
      return {
        filename: '',
        jobLoading: false,
        productionOnly: false,
        jobTemplates: [],
        jobTemplatesLoading: false,
        selectedJobTemplateId: null,
        templates: [],
        selectedTemplate: {
          props: [],
        },
        nexrenderJob: {
          template: {
            src: '',
            composition: '',
            outputModule: 'H.264 - Match Render Settings - 15 Mbps',
            outputExt: 'mp4',
            settingsTemplate: 'Best Settings',
            continueOnMissing: false,
          },
          assets: [],
          actions: {
            postrender: [
              {
                module: '@nexrender/action-upload',
                provider: 'ftp',
                params: {
                  host: 'storage007.noisypeak.com',
                  port: 21,
                  user: 'template007',
                  password: 'YfnP14iRa',
                  output: '/',
                },
              },
            ],
          },
        },
        content: '',
        view: null,
        polling: null,
        propSelectionDialog: false,
        propSelectionType: '',
        selectedProp: {},
        fonts: [],
        fontsLoading: false,
        videos: videoAssets,
        audios: audioAssets,
        images: imageAssets,
      }
    },
    computed: {
      loading () {
        return this.$store.getters.isDataLoaded
      },
      isFormValid () {
        if (!this.selectedTemplate.id) {
          return false
        }
        if (this.filename.length < 3) {
          return false
        }
        return true
      },
      isSuper () {
        return this.$store.getters.isSuperadmin
      },
      filteredTemplates () {
        if (!this.productionOnly) {
          return this.templates
        }

        return this.templates.filter(
          (template) =>
            !!this.getTemplateProdType(template),
        )
      },
      currentAssets () {
        switch (this.propSelectionType) {
          case 'Image':
            return this.images
          case 'Video':
            return this.videos
          case 'Audio':
            return this.audios
          default:
            return []
        }
      },
      fontFaceOptions () {
        const values = []
        const used = {}

        for (let i = 0; i < this.fonts.length; i += 1) {
          const faces = Array.isArray(this.fonts[i].faces)
            ? this.fonts[i].faces
            : []

          for (let j = 0; j < faces.length; j += 1) {
            const postscriptName =
              faces[j].postscriptName ||
              faces[j].postscript_name ||
              ''

            if (postscriptName && !used[postscriptName]) {
              used[postscriptName] = true
              values.push(postscriptName)
            }
          }
        }

        return values.sort((a, b) => a.localeCompare(b))
      },
    },
    watch: {},
    mounted () {
      this.content = JSON.stringify(this.nexrenderJob, null, ' ')

      this.view = new EditorView({
        doc: this.content,
        extensions: [basicSetup, json(), linterExtension],
        parent: this.$refs.editor,
      })

      this.loadJobTemplates()
    },
    created () {
      this.loadData()
      this.loadFonts()
    },
    beforeDestroy () {
      clearInterval(this.polling)
    },
    methods: {
      cloneJobTemplate (template) {
        return JSON.parse(JSON.stringify(template || {}))
      },
      parseJobTemplateValue (value) {
        if (!value) return null
        if (typeof value === 'object') return this.cloneJobTemplate(value)

        try {
          return JSON.parse(value)
        } catch (error) {
          return null
        }
      },
      getJobTemplateResultRows (result) {
        if (Array.isArray(result)) return result
        if (result && Array.isArray(result.items)) return result.items
        return []
      },
      loadJobTemplates () {
        this.jobTemplatesLoading = true

        this.$store.dispatch('nexrenderJobTemplates', {
          $orderby: 'id',
        })
          .then((result) => {
            const rows = this.getJobTemplateResultRows(result)

            this.jobTemplates = rows
              .map((row) => ({
                id: row.id,
                name: row.name || '',
                is_default: !!(row.is_default || row.isDefault),
                template: this.parseJobTemplateValue(row.template),
              }))
              .filter((row) => !!row.template)

            if (!this.jobTemplates.length) return

            const defaultTemplate =
              this.jobTemplates.find((item) => item.is_default) ||
              this.jobTemplates[0]

            this.selectedJobTemplateId = defaultTemplate.id
            this.applySelectedJobTemplate()
          })
          .catch((errorMsg) => {
            this.$store.commit('showSnackbar', {
              text: errorMsg && errorMsg.message ? errorMsg.message : errorMsg,
              timeout: 6000,
              color: 'error',
            })
          })
          .finally(() => {
            this.jobTemplatesLoading = false
          })
      },
      getSelectedJobTemplate () {
        return this.jobTemplates.find(
          (item) => item.id === this.selectedJobTemplateId,
        )
      },
      applySelectedJobTemplate () {
        const selectedJobTemplate = this.getSelectedJobTemplate()

        if (!selectedJobTemplate || !selectedJobTemplate.template) return

        this.nexrenderJob = this.cloneJobTemplate(selectedJobTemplate.template)

        if (!Array.isArray(this.nexrenderJob.assets)) {
          this.$set(this.nexrenderJob, 'assets', [])
        }

        if (this.selectedTemplate && this.selectedTemplate.id) {
          this.populateJobFromAEPTemplate()
          return
        }

        this.filenameChanged()
        this.jobChanged()
      },
      jobTemplateChanged () {
        this.applySelectedJobTemplate()
      },
      getFileExtension (value) {
        const cleanValue =
          String(value || '')
            .trim()
            .split('?')[0]
            .split('#')[0]

        const fileName =
          cleanValue
            .replace(/\\/g, '/')
            .split('/')
            .pop()

        if (!fileName) {
          return ''
        }

        const dotIndex =
          fileName.lastIndexOf('.')

        if (
          dotIndex <= 0 ||
          dotIndex === fileName.length - 1
        ) {
          return ''
        }

        return fileName
          .substring(dotIndex + 1)
          .toLowerCase()
      },
      getOutputExtension () {
        const templateExt =
          this.nexrenderJob &&
          this.nexrenderJob.template &&
          this.nexrenderJob.template.outputExt
            ? String(
              this.nexrenderJob.template.outputExt,
            )
              .trim()
              .replace(/^\./, '')
              .toLowerCase()
            : ''

        const postrender =
          this.nexrenderJob &&
          this.nexrenderJob.actions &&
          Array.isArray(
            this.nexrenderJob.actions.postrender,
          )
            ? this.nexrenderJob.actions.postrender
            : []

        for (
          let i = postrender.length - 1;
          i >= 0;
          i -= 1
        ) {
          const inputExt =
            this.getFileExtension(
              postrender[i] &&
                postrender[i].input,
            )

          if (inputExt) {
            return inputExt
          }
        }

        return templateExt || 'mp4'
      },
      populateJobFromAEPTemplate () {
        if (!this.selectedTemplate || !this.selectedTemplate.id) {
          this.jobChanged()
          return
        }

        if (!this.nexrenderJob.template) {
          this.$set(this.nexrenderJob, 'template', {})
        }

        this.nexrenderJob.template.src = this.selectedTemplate.url
        this.nexrenderJob.template.composition = this.selectedTemplate.root.name

        this.$set(
          this.nexrenderJob,
          'priority',
          Number(this.selectedTemplate.priority || 0),
        )

        if (!Array.isArray(this.nexrenderJob.assets)) {
          this.$set(this.nexrenderJob, 'assets', [])
        }

        this.filename =
          this.selectedTemplate.name +
          '_' +
          moment.utc().format('DDMMYYHHmm') +
          '.' +
          this.getOutputExtension()

        this.filenameChanged()

        for (let i = 0; i < this.selectedTemplate.props.length; i += 1) {
          this.propChanged(this.selectedTemplate.props[i])
        }

        if (this.selectedTemplate.files && this.selectedTemplate.files.aep) {
          this.setScript()
        } else {
          this.jobChanged()
        }
      },
      getTemplateProdType (template) {
        if (!template) {
          return ''
        }

        return String(
          template.prodType ||
            template.prod_type ||
            template.productionType ||
            template.production_type ||
            '',
        ).trim()
      },
      productionOnlyChanged () {
        if (
          this.productionOnly &&
          this.selectedTemplate.id &&
          !this.getTemplateProdType(
            this.selectedTemplate,
          )
        ) {
          this.selectedTemplate = {
            props: [],
          }

          this.filename = ''

          const selectedJobTemplate = this.getSelectedJobTemplate()
          if (selectedJobTemplate && selectedJobTemplate.template) {
            this.nexrenderJob = this.cloneJobTemplate(selectedJobTemplate.template)
          }

          this.filenameChanged()
          this.jobChanged()
        }
      },
      templateChanged () {
        const selectedJobTemplate = this.getSelectedJobTemplate()

        if (selectedJobTemplate && selectedJobTemplate.template) {
          this.nexrenderJob = this.cloneJobTemplate(selectedJobTemplate.template)
        }

        if (!Array.isArray(this.nexrenderJob.assets)) {
          this.$set(this.nexrenderJob, 'assets', [])
        }

        this.populateJobFromAEPTemplate()
      },
      filenameChanged () {
        if (this.nexrenderJob.actions && this.nexrenderJob.actions.postrender && this.nexrenderJob.actions.postrender.length) {
          for (let i = 0; i < this.nexrenderJob.actions.postrender.length; i += 1) {
            const act = this.nexrenderJob.actions.postrender[i]
            if (act.module === '@nexrender/action-upload') {
              act.params.output = '/' + this.filename.replace(/\//g, '_')
              this.jobChanged()
            }
          }
        }
      },
      propChanged (prop) {
        for (let i = 0; i < this.nexrenderJob.assets.length; i += 1) {
          const asset = this.nexrenderJob.assets[i]
          if (asset.layerName === prop.layer && asset.composition === prop.comp) {
            if (prop.value) {
              this.processAsset(prop, asset)
            } else {
              this.nexrenderJob.assets.splice(i, 1)
            }

            this.jobChanged()
            return
          }
        }

        if (!prop.value) {
          return
        }

        const newAsset = {
          layerName: prop.layer,
          composition: prop.comp,
        }
        this.processAsset(prop, newAsset)

        this.nexrenderJob.assets.push(newAsset)
        this.jobChanged()
      },
      processAsset (prop, asset) {
        if (this.isFontProp(prop)) {
          asset.type = 'data'
          asset.value = prop.value
          asset.property = 'Source Text'

          delete asset.src
          delete asset.name
          delete asset.useOriginal
          return
        }

        if (this.isColorProp(prop)) {
          asset.type = 'data'
          asset.value = this.hexToNexrenderColor(
            prop.colorHex || prop.value,
          )
          asset.property = 'Source Text'

          delete asset.src
          delete asset.name
          delete asset.useOriginal
          return
        }

        if (prop.type === 'Text') {
          asset.type = 'data'
          asset.value = prop.value
          asset.property = 'Source Text'

          delete asset.src
          delete asset.name
          delete asset.useOriginal
          return
        }

        asset.type = prop.type.toLowerCase()
        asset.src = prop.value
        asset.name = this.getFileName(prop.value)

        if (String(prop.value || '').startsWith('file')) {
          asset.useOriginal = true
        } else {
          delete asset.useOriginal
        }

        delete asset.value
        delete asset.property
      },
      isFontProp (prop) {
        return /\.FONT\./i.test(
          String(prop && prop.layer ? prop.layer : ''),
        )
      },
      isColorProp (prop) {
        return /\.COL\./i.test(
          String(prop && prop.layer ? prop.layer : ''),
        )
      },
      isUploadProp (prop) {
        return (
          !this.isFontProp(prop) &&
          !this.isColorProp(prop) &&
          prop.type !== 'Text'
        )
      },
      normalizeHex (value) {
        const raw = String(value || '').trim().toUpperCase()

        if (/^#[0-9A-F]{8}$/.test(raw)) {
          return raw.slice(0, 7)
        }

        if (/^[0-9A-F]{8}$/.test(raw)) {
          return `#${raw.slice(0, 6)}`
        }

        if (/^#[0-9A-F]{6}$/.test(raw)) return raw
        if (/^[0-9A-F]{6}$/.test(raw)) return `#${raw}`

        if (/^#[0-9A-F]{3}$/.test(raw)) {
          return (
            `#${raw[1]}${raw[1]}` +
            `${raw[2]}${raw[2]}` +
            `${raw[3]}${raw[3]}`
          )
        }

        return ''
      },
      colorChannelsToHex (channels) {
        const values = channels.map((item) =>
          Math.round(
            Math.max(0, Math.min(1, Number(item))) * 255,
          )
            .toString(16)
            .padStart(2, '0'),
        )

        return `#${values.join('')}`.toUpperCase()
      },
      nexrenderColorToHex (value) {
        if (Array.isArray(value)) {
          return this.colorChannelsToHex(value)
        }

        const normalizedHex = this.normalizeHex(value)
        if (normalizedHex) return normalizedHex

        const channels = String(value || '')
          .split(',')
          .map((item) => Number(item.trim()))

        if (
          channels.length === 3 &&
          channels.every((item) =>
            Number.isFinite(item) && item >= 0 && item <= 1,
          )
        ) {
          return this.colorChannelsToHex(channels)
        }

        return '#000000'
      },
      getPropColorHex (prop) {
        return this.nexrenderColorToHex(
          prop.colorHex || prop.value || '#000000',
        )
      },
      colorChanged (prop, value) {
        const hex = this.nexrenderColorToHex(value)

        this.$set(prop, 'colorHex', hex)
        this.$set(prop, 'value', hex)
        this.propChanged(prop)
      },
      formatNexrenderColorChannel (value) {
        return Number((value / 255).toFixed(4)).toString()
      },
      hexToNexrenderColor (value) {
        const hex = this.nexrenderColorToHex(value).replace('#', '')
        const r = parseInt(hex.slice(0, 2), 16)
        const g = parseInt(hex.slice(2, 4), 16)
        const b = parseInt(hex.slice(4, 6), 16)

        return [
          this.formatNexrenderColorChannel(r),
          this.formatNexrenderColorChannel(g),
          this.formatNexrenderColorChannel(b),
        ].join(', ')
      },
      getFileName (fullPath) {
        if (!fullPath || typeof fullPath !== 'string') return ''
        let path = fullPath.replace(/\\/g, '/')
        path = path.split('?')[0].split('#')[0]
        const filename = path.split('/').pop() || ''
        return filename
      },
      jobChanged () {
        this.content = JSON.stringify(this.nexrenderJob, null, ' ')

        const transaction = this.view.state.update({
          changes: { from: 0, to: this.view.state.doc.length, insert: this.content },

        })
        this.view.dispatch(transaction)
      },
      loadFonts () {
        this.fontsLoading = true

        const params = {
          $expand: 'faces',
          $orderby: 'sort',
        }

        this.$store.dispatch('fonts', params)
          .then((fonts) => {
            this.fonts = fonts || []
          })
          .catch((errorMsg) => {
            this.fonts = []
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
          })
          .finally(() => {
            this.fontsLoading = false
          })
      },
      loadData () {
        const params = {
          $orderby: '-id',
        }
        const aepReq = this.$store.dispatch('aepProjects', params)

        Promise.all([aepReq])
          .then((result) => {
            const projs = result[0] || []

            const items = []
            for (let i = 0; i < projs.length; i += 1) {
              const rawItem = projs[i]
              const item = processAEPProject(rawItem)

              item.prodType =
                this.getTemplateProdType(item) ||
                this.getTemplateProdType(rawItem)

              for (let j = 0; j < item.props.length; j += 1) {
                if (item.props[j].type === 'Media') {
                  item.props[j].type = 'Video'
                }
                item.props[j].value =
                  item.props[j].defaultValue ||
                  ''

                item.props[j].file = null
                item.props[j].fileLoading = false

                if (this.isColorProp(item.props[j])) {
                  this.$set(
                    item.props[j],
                    'colorHex',
                    this.nexrenderColorToHex(
                      item.props[j].value || '#000000',
                    ),
                  )
                }
              }
              items.push(item)
            }

            this.templates = items
          })
          .catch((errorMsg) => {
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
          })
          .finally(() => this.$store.commit('resolvePageLoader'))
      },
      pollData () {
        this.polling = setInterval(() => {
          this.loadData(true)
        }, 2000)
      },
      selectValue (prop, type) {
        this.propSelectionDialog = true
        this.propSelectionType = type
        this.selectedProp = prop
      },
      setProp (value, type) {
        this.selectedProp.type = type
        this.selectedProp.value = value
        this.propSelectionDialog = false
        this.propChanged(this.selectedProp)
      },
      getRandomItem (array) {
        if (!array || array.length === 0) {
          return ''
        }
        const randomIndex = Math.floor(Math.random() * array.length)
        return array[randomIndex]
      },
      randomInteger (min, max) {
        return Math.floor(Math.random() * (max - min + 1)) + min
      },
      randomData () {
        for (
          let i = 0;
          i < this.selectedTemplate.props.length;
          i += 1
        ) {
          const prop = this.selectedTemplate.props[i]

          if (this.isFontProp(prop)) {
            prop.value = this.getRandomItem(this.fontFaceOptions)
            this.propChanged(prop)
            continue
          }

          if (this.isColorProp(prop)) {
            const value =
              (
                '#' +
                Math.random()
                  .toString(16)
                  .slice(2, 8)
                  .padEnd(6, '0')
              )
                .toUpperCase()

            this.colorChanged(prop, value)
            continue
          }

          if (
            String(prop.layer || '').toUpperCase() ===
            'NP.DYN.NP_LOGO.TXT'
          ) {
            prop.value = String(randomInteger(0, 1))
            this.propChanged(prop)
            continue
          }

          if (prop.type === 'Text') {
            prop.value =
              'Some text ' + randomInteger(1000, 100000)
          }

          if (prop.type === 'Image') {
            prop.value = this.getRandomItem(this.images)
          }

          if (prop.type === 'Video') {
            prop.value = this.getRandomItem(this.videos)
          }

          if (prop.type === 'Audio') {
            prop.value = this.getRandomItem(this.audios)
          }

          this.propChanged(prop)
        }
      },
      startJob () {
        if (
          !this.isFormValid ||
          this.jobLoading
        ) {
          return
        }

        let nexrenderJob

        try {
          nexrenderJob =
            JSON.parse(
              this.view.state.doc.toString(),
            )
        } catch (error) {
          this.$store.commit(
            'showSnackbar',
            {
              text:
                'Invalid JSON: ' +
                error,
              timeout: 6000,
              color: 'error',
            },
          )

          return
        }

        const data = {
          name:
            this.selectedTemplate.name +
            '-' +
            this.filename,
          job: nexrenderJob,
          template_id:
            this.selectedTemplate.id,
        }

        this.jobLoading = true

        this.$store.dispatch(
          'createNexrenderJob',
          data,
        )
          .then(() => {
            this.$store.commit(
              'showSnackbar',
              {
                text:
                  'The job has been successfully started',
                timeout: 3000,
                color: 'success',
              },
            )

            if (
              this.$route.name !==
              'NexrenderPage'
            ) {
              this.$router.replace(
                {
                  name: 'NexrenderPage',
                },
                () => {},
                () => {},
              )
            }
          })
          .catch((errorMsg) => {
            this.$store.commit(
              'showSnackbar',
              {
                text:
                  errorMsg &&
                  errorMsg.message
                    ? errorMsg.message
                    : errorMsg,
                timeout: 6000,
                color: 'error',
              },
            )
          })
          .finally(() => {
            this.jobLoading = false
          })
      },
      fileUpload (prop, key) {
        if (prop.fileLoading) return
        prop.fileLoading = true

        const file = new FormData()
        file.append('uploadfile', prop.file, prop.file.name)

        const params = {
          file,
          name: prop.file.name,
        }

        this.$store.dispatch('ftpUpload', params)
          .then((result) => {
            prop.fileLoading = false
            prop.value = result.url
            this.propChanged(prop)
          })
          .catch((errorMsg) => {
            prop.fileLoading = false
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
          })
      },
      setScript () {
        this.nexrenderJob.template.src = 'file:///N:' + this.selectedTemplate.files.dir + '/' + this.selectedTemplate.files.aep
        this.filenameChanged()

        const dirPath = this.selectedTemplate.files.dir + '/' + this.selectedTemplate.files.aep
        const lastSlashIndex = dirPath.lastIndexOf('/')
        const v = dirPath.substring(0, lastSlashIndex)

        this.nexrenderJob.assets = this.nexrenderJob.assets.filter(
          (asset) =>
            !(
              asset &&
              asset.type === 'script' &&
              asset.src === 'file:///N:/customisations/find-replace-missing-items.jsx'
            ),
        )

        this.nexrenderJob.assets.push({
          src: 'file:///N:/customisations/find-replace-missing-items.jsx',
          type: 'script',
          useOriginal: true,
          parameters: [
            {
              key: 'TEMPLATEDIR',
              value: 'N:' + v.replace(/\//g, '\\'),
            },
          ],
        })
        this.jobChanged()
      },
    },
  }
</script>

<style lang="scss">
.cm-editor * {
  font-size: 13px !important;
}

.nexrender-prop-row {
  display: grid;
  grid-template-columns: 110px minmax(350px, 1fr) 30px;
  gap: 10px;
  align-items: center;
}

.nexrender-prop-type {
  width: 110px;
  max-width: 110px;
}

.nexrender-prop-value {
  min-width: 350px;
}

.nexrender-prop-upload {
  width: 30px;
}

.nexrender-color-swatch {
  display: inline-block;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  border: 1px solid rgba(0, 0, 0, 0.22);
  border-radius: 3px;
}

.template-option {
  min-width: 0;
  display: flex;
  align-items: center;
}

.template-option-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.job-template-option {
  min-width: 0;
  display: flex;
  align-items: center;
}

.hovered-link:hover {
  color: #0a53be;
}
</style>
